import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import './Register.css';
import coinLogo from '../assets/COINLogo.png';
import { useAuth } from '../contexts/AuthContext';
import { getStripe } from '../utils/stripe';

// Read Price IDs from environment variables
const MONTHLY_PRICE_ID = process.env.REACT_APP_MONTHLY_PRICE_ID;
const YEARLY_PRICE_ID = process.env.REACT_APP_YEARLY_PRICE_ID;

function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [communicationsOptIn, setCommunicationsOptIn] = useState(true);
  const [selectedPriceId, setSelectedPriceId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuthStatus, isAuthenticated, user } = useAuth();
  // A signed-in free-tier account already has an email and password, so this
  // page becomes a plan picker that maps checkout back to that account.
  const isUpgrade = isAuthenticated && !user?.is_subscribed;
  const isTrialEligible = !isUpgrade || user?.sub_tier !== 'paid';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const normalizedEmail = email.trim().toLowerCase();

    if (!termsAccepted) {
      setError('You must accept the Terms and Conditions to continue');
      return;
    }
    if (!isUpgrade) {
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      if (password.length < 6) {
        setError('Password must be at least 6 characters long');
        return;
      }
    }
    if (!selectedPriceId) {
      setError('Please select a subscription plan.');
      return;
    }

    setIsLoading(true);
    try {
      const endpoint = isUpgrade
        ? '/api/payment/create-upgrade-session'
        : '/api/payment/create-verification-session';
      const payload = isUpgrade
        ? { price_id: selectedPriceId }
        : {
            email: normalizedEmail,
            password,
            email_opt_in: communicationsOptIn,
            price_id: selectedPriceId
          };
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.sessionId) {
        const stripe = await getStripe();
        const { error: stripeError } = await stripe.redirectToCheckout({
          sessionId: data.sessionId,
        });
        if (stripeError) {
          console.error("Stripe redirection error:", stripeError);
          setError(`Payment Error: ${stripeError.message}`);
          setIsLoading(false);
        }
      } else {
        setError(data.error || 'Failed to start registration process. Please try again.');
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Registration/Checkout error:', err);
      setError('An error occurred. Please check your connection and try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className={`register-box`}>
        <div className="logo-container">
          <img src={coinLogo} alt="COIN Logo" />
        </div>
        <h2>{isUpgrade ? 'Upgrade for Full Access to C.O.I.N. Grading' : 'Start Your Free 7-Day Trial of C.O.I.N.'}</h2>
        {isUpgrade && (
          <p className="upgrade-account-note">
            Upgrading <strong>{user?.email}</strong>. Your existing account and saved
            searches carry over — no new sign-up needed.
          </p>
        )}
        {error && <div className="error-message">{error}</div>}
        {isLoading && <div className="loading-message">Processing...</div>}

        <form onSubmit={handleSubmit}>
          {!isUpgrade && <>
          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password:</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password (min 6 chars)"
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="confirm-password">Confirm Password:</label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm your password"
              disabled={isLoading}
            />
          </div>
          </>}

          <div style={{ textAlign: 'center', marginBottom: '15px', color: 'white' }}>
            {isTrialEligible ? <>
              Choose your plan for after your free trial ends.<br />
              No charges today. Full access during your 7-day free trial.
            </> : <>
              Choose your plan to restore full access.<br />
              Your subscription starts today and is charged at checkout.
            </>}
            {' '}Cancel anytime. No hidden fees.
          </div>

          <div className="plan-selection-container">
            <label className="plan-label">Select Subscription Plan:</label>
            <div className="plan-options" style={{ justifyContent: 'center' }}>
              <div
                className={`plan-option ${selectedPriceId === MONTHLY_PRICE_ID ? 'selected' : ''}`}
                onClick={() => !isLoading && setSelectedPriceId(MONTHLY_PRICE_ID)}
              >
                <div className="plan-title">Monthly</div>
                <div className="plan-trial-info">
                  {isTrialEligible ? '7-Day Free Trial, then' : 'Billed starting today'}
                </div>
                <div className="plan-price">$49.99</div>
                <div className="plan-billing">per month</div>
              </div>
            </div>
          </div>

          {!isUpgrade && <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={communicationsOptIn}
                onChange={(e) => setCommunicationsOptIn(e.target.checked)}
                disabled={isLoading}
              />
              <span>Opt-in to communications regarding C.O.I.N.</span>
            </label>
          </div>}
          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                required
                disabled={isLoading}
              />
              <br />
              <span>By signing up, you agree to these <Link to="/terms">Terms</Link>. <br /><br />
              {isTrialEligible
                ? 'Valid card required to activate free trial. $0 due today.'
                : 'Your selected subscription is charged when you continue to checkout.'}<br /> <br />
              Questions? support@cardboardprofit.com.<br /><br />Cardboard Edge LLC</span>
            </label>
          </div>

          <button
            type="submit"
            className="register-button"
            disabled={!termsAccepted || !selectedPriceId || isLoading}
          >
            {isLoading ? 'Processing...' : isTrialEligible ? 'Start Free Trial' : 'Subscribe Now'}
          </button>
        </form>

        <div className="auth-links">
          {!isUpgrade && <Link to="/login">Already have an account? Log in</Link>}
          <Link to="/">Back to Home</Link>
        </div>
      </div>
    </div>
  );
}

export default Register; 
